import { ToastProps, Types } from '@nimbus-ds/components';
import { TFunction } from 'i18next';

type TToast = (toast: Omit<ToastProps, 'autoClose'>) => void;

export const alertMessagesHelper = {
  error: {
    save: 'General.AlertErrorSave',
    sync: 'General.AlertErrorSync',
    update: 'General.AlertErrorUpdate',
    delete: 'General.AlertErrorDelete',
    formFieldsInvalid: 'General.AlertErrorFormFieldsInvalid',
    server: 'General.AlertErrorServer',
    notFound: 'General.AlertErrorNotFound',
    notPaid: 'APP_NOT_PAID',
    profileIncomplete: 'STORE_PROFILE_INCOMPLETE',
  },
  success: {
    save: 'General.AlertSuccessSave',
    sync: 'General.AlertSuccessSync',
    update: 'General.AlertSuccessUpdate',
    delete: 'General.AlertSuccessDelete',
  },
  progress: {
    save: 'General.AlertProgressSave',
    update: 'General.AlertProgressUpdate',
    delete: 'General.AlertProgressDelete',
  },
};

export class ToastManager {
  private activeToasts = new Set<string>();
  private toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  private preventDuplicates = true; // Controle global
  private duplicateTimeout = 5000; // Timeout padrão

  constructor(
    private addToast: TToast,
    private t: TFunction,
    options?: {
      preventDuplicates?: boolean;
      duplicateTimeout?: number;
    },
  ) {
    if (options) {
      this.preventDuplicates = options.preventDuplicates ?? true;
      this.duplicateTimeout = options.duplicateTimeout ?? 5000;
    }
  }

  // Método para configurar o comportamento anti-duplicação
  setDuplicatePrevention(enabled: boolean, timeout?: number) {
    this.preventDuplicates = enabled;
    if (timeout !== undefined) {
      this.duplicateTimeout = timeout;
    }
  }

  private notify(
    type: Types,
    key: string,
    options?: { preventDuplicate?: boolean },
  ) {
    const toastId = this.t(key);
    const toastText = this.t(key);

    // Verifica se deve prevenir duplicação (global ou local)
    const shouldPreventDuplicate =
      options?.preventDuplicate ?? this.preventDuplicates;

    if (shouldPreventDuplicate && this.activeToasts.has(toastText)) {
      return; // Não adiciona toast duplicado
    }

    // Adiciona o toast à lista de ativos apenas se estiver prevenindo duplicação
    if (shouldPreventDuplicate) {
      this.activeToasts.add(toastText);

      // Configura timeout para remover o toast da lista após um tempo
      const timeout = setTimeout(() => {
        this.activeToasts.delete(toastText);
        this.toastTimeouts.delete(toastText);
      }, this.duplicateTimeout);

      this.toastTimeouts.set(toastText, timeout);
    }

    this.addToast({
      id: toastId,
      type,
      text: toastText,
      position: 6,
    });
  }

  // Método para forçar a remoção de um toast específico da lista
  removeFromActive(key: string) {
    const toastText = this.t(key);
    this.activeToasts.delete(toastText);

    const timeout = this.toastTimeouts.get(toastText);
    if (timeout) {
      clearTimeout(timeout);
      this.toastTimeouts.delete(toastText);
    }
  }

  // Método para limpar todos os toasts ativos
  clearActiveToasts() {
    this.activeToasts.clear();
    this.toastTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.toastTimeouts.clear();
  }

  // Métodos com opção de controle de duplicação
  success = (key: string, options?: { preventDuplicate?: boolean }) =>
    this.notify('success', key, options);

  error = (key: string, options?: { preventDuplicate?: boolean }) =>
    this.notify('danger', key, options);

  info = (key: string, options?: { preventDuplicate?: boolean }) =>
    this.notify('primary', key, options);

  progress = (key: string, options?: { preventDuplicate?: boolean }) =>
    this.notify('progress', key, options);

  private actionToast(
    map: Record<string, string>,
    type: Types,
    action: keyof typeof map,
    options?: { preventDuplicate?: boolean },
  ) {
    this.notify(type, map[action], options);
  }

  successAction(
    action: keyof typeof alertMessagesHelper.success,
    options?: { preventDuplicate?: boolean },
  ) {
    this.actionToast(alertMessagesHelper.success, 'success', action, options);
  }

  errorAction(
    action: keyof typeof alertMessagesHelper.error,
    options?: { preventDuplicate?: boolean },
  ) {
    this.actionToast(alertMessagesHelper.error, 'danger', action, options);
  }

  progressAction(
    action: keyof typeof alertMessagesHelper.progress,
    options?: { preventDuplicate?: boolean },
  ) {
    this.actionToast(alertMessagesHelper.progress, 'progress', action, options);
  }
}
